import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
    Sparkles,
    ClipboardList,
    Target,
    TrendingUp,
    Package,
    CheckCircle2,
} from "lucide-react";
import { Card, CardContent } from "../../components/Card";

// Componente StrategyCard (imported from Leadmagnet.jsx pattern)
const StrategyCard = ({ number, title, description, theme }) => (
    <Card
        sx={{
            height: "100%",
        }}
    >
        <CardContent sx={{ padding: 3 }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    marginBottom: 2,
                }}
            >
                <Box
                    sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        backgroundColor: theme.palette.primary.main,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: "#ffffff",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                        }}
                    >
                        {number}
                    </Typography>
                </Box>
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                    }}
                >
                    {title}
                </Typography>
            </Box>
            <Typography
                variant="body2"
                sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.6,
                }}
            >
                {description}
            </Typography>
        </CardContent>
    </Card>
);

const LeadmagnetEjemplo = () => {
    const theme = useTheme();

    // Datos de ejemplo hardcodeados
    const strategyData = {
        beneficios: [
            "Aumento de la visibilidad en búsquedas locales, lo que atraerá más clientes al bar.",
            "Mejora de la reputación online a través de la gestión activa de reseñas y comentarios.",
            "Incremento en la captación de leads mediante promociones y landing pages específicas.",
        ],
        estrategia: {
            atraccion:
                "Implementar una estrategia SEO local centrada en la optimización de Google My Business para mejorar la posición en los resultados de búsqueda y atraer tráfico local. Esto incluye la creación de contenido relevante en el blog sobre eventos locales y promociones del bar, así como la optimización de palabras clave relacionadas con 'bares cerca de mí'. Se realizarán campañas de publicidad en Google Ads y redes sociales para atraer a residentes y trabajadores de la zona.",
            captacion:
                "Crear Landing Pages específicas para promociones y eventos en el bar que capturen datos de contacto de los visitantes. Utilizar campañas en redes sociales y Google Ads para dirigir tráfico a estas páginas. Se implementarán Call to Action claras para facilitar el contacto y fomentar la reserva de mesas. Además, se crearán cupones o promociones especiales para incentivar la visita.",
            conversion:
                "Realizar un seguimiento de los leads generados y optimizar las conversiones mediante la implementación de un sistema de gestión de clientes que permita enviar promociones personalizadas y mantener el contacto con los clientes. Se fomentará la fidelización a través de un programa de recompensas que incentive a los clientes a volver. También se crearán campañas de email marketing y se monitorizará la reputación online, respondiendo a reseñas y comentarios para mejorar la percepción del bar.",
        },
        diagnostico:
            "El bar tiene una sólida trayectoria de más de 10 años, pero enfrenta desafíos en aumentar su visibilidad local y atraer más clientes del área circundante. La competencia en el sector es intensa y la reputación online necesita fortalecerse para diferenciarse. Actualmente, la presencia en Google Maps y en búsquedas locales es limitada, lo que afecta la captación de nuevos clientes.",
        pack_recomendado: {
            nombre: "Starter",
            precio: "269 € / mes",
            justificacion:
                "El pack Starter es el más adecuado para este bar ya que está diseñado específicamente para negocios físicos que buscan aumentar su visibilidad en el área local. Incluye la optimización de Google Business Profile, que es crucial para aparecer en búsquedas locales y mejorar la captación de clientes. Además, proporciona contenido mensual en el blog, lo que ayudará a posicionar al bar como un referente en la comunidad. Los servicios de monitorización de palabras clave y la auditoría web también son beneficiosos para asegurar que la estrategia de marketing digital sea efectiva.",
        },
    };

    return (
        <Box
            sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflowY: "auto",
                overflowX: "hidden",
                backgroundColor: theme.palette.background.default,
                padding: { xs: 2, md: 4 },
            }}
        >
            <Box
                sx={{
                    maxWidth: "1000px",
                    margin: "0 auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        pt: 4,
                        pb: 0,
                    }}
                >
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: `0 8px 32px ${theme.palette.primary.main}40`,
                        }}
                    >
                        <Sparkles size={28} color="#ffffff" />
                    </Box>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            fontSize: { xs: "1.5rem", md: "2rem" },
                        }}
                    >
                        ¡Tu Plan de Marketing Personalizado está Listo!
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: theme.palette.text.secondary,
                            maxWidth: "600px",
                        }}
                    >
                        Basado en tus respuestas, hemos diseñado una estrategia para
                        impulsar tu negocio.
                    </Typography>
                </Box>

                {/* Diagnóstico Actual */}
                <Card
                    sx={{
                        backgroundColor: `${theme.palette.primary.main}08`,
                    }}
                >
                    <CardContent sx={{ padding: 3 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                marginBottom: 1,
                            }}
                        >
                            <ClipboardList size={20} color={theme.palette.primary.main} />
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: theme.palette.primary.main,
                                }}
                            >
                                Diagnóstico Actual
                            </Typography>
                        </Box>
                        <Typography
                            variant="body1"
                            sx={{
                                color: theme.palette.text.primary,
                                lineHeight: 1.7,
                            }}
                        >
                            {strategyData.diagnostico}
                        </Typography>
                    </CardContent>
                </Card>

                {/* Estrategia Recomendada */}
                <Box>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            marginBottom: 3,
                        }}
                    >
                        <Target size={22} color={theme.palette.text.primary} />
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                            }}
                        >
                            Estrategia Recomendada: Método SERSEO
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                            gap: 3,
                        }}
                    >
                        {/* Atracción */}
                        <StrategyCard
                            number="1"
                            title="Atracción"
                            description={strategyData.estrategia.atraccion}
                            theme={theme}
                        />

                        {/* Captación */}
                        <StrategyCard
                            number="2"
                            title="Captación"
                            description={strategyData.estrategia.captacion}
                            theme={theme}
                        />

                        {/* Conversión */}
                        <StrategyCard
                            number="3"
                            title="Conversión"
                            description={strategyData.estrategia.conversion}
                            theme={theme}
                        />
                    </Box>
                </Box>

                {/* Pack Recomendado y Beneficios */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: 3,
                    }}
                >
                    {/* Pack Recomendado */}
                    <Card
                        sx={{
                            backgroundColor: `${theme.palette.primary.main}08`,
                            border: `1px solid ${theme.palette.primary.light}40`,
                        }}
                    >
                        <CardContent sx={{ padding: 3 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    marginBottom: 2,
                                }}
                            >
                                <Package size={20} color={theme.palette.primary.main} />
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    Pack SERSEO Recomendado
                                </Typography>
                            </Box>

                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    color: theme.palette.primary.main,
                                    marginBottom: 0.5,
                                }}
                            >
                                {strategyData.pack_recomendado.nombre}
                            </Typography>

                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 500,
                                    color: theme.palette.text.secondary,
                                    marginBottom: 2,
                                }}
                            >
                                {strategyData.pack_recomendado.precio}
                            </Typography>

                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.text.primary,
                                    lineHeight: 1.6,
                                }}
                            >
                                {strategyData.pack_recomendado.justificacion}
                            </Typography>
                        </CardContent>
                    </Card>

                    {/* Beneficios Esperados */}
                    <Card
                        sx={{
                            backgroundColor: `${theme.palette.success.main}08`,
                            border: `1px solid ${theme.palette.success.light}40`,
                        }}
                    >
                        <CardContent sx={{ padding: 3 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    marginBottom: 2,
                                }}
                            >
                                <TrendingUp size={20} color={theme.palette.success.main} />
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    Beneficios Esperados
                                </Typography>
                            </Box>

                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                {strategyData.beneficios.map((beneficio, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 1.5,
                                        }}
                                    >
                                        <CheckCircle2
                                            size={18}
                                            color={theme.palette.success.main}
                                            style={{ marginTop: 2, flexShrink: 0 }}
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            {beneficio}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Espacio inferior */}
                <Box sx={{ paddingBottom: 4 }} />
            </Box>
        </Box>
    );
};

export default LeadmagnetEjemplo;
